import React, { useState, useEffect } from "react";
import api from "../services/api";

const CourseTable = () => {
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [preferredIntake, setPreferredIntake] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    let isMounted = true;

    const fetchCourses = async () => {
      try {
        const authToken = sessionStorage.getItem("token");

        if (!authToken) {
          console.log("No auth token found");
          setLoading(false);
          return;
        }

        const response = await api({
          url: `/api/course/list`,
          method: "GET",
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (isMounted && response.status === 200) {
          setCourses(response.data.data);
          setFilteredCourses(response.data.data);
        }
      } catch (error) {
        if (isMounted) {
          console.log("Error fetching courses:", error.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCourses();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const filtered = courses.filter((course) =>
      course.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredCourses(filtered);
  }, [searchQuery, courses]);

  const openDialog = (course) => {
    setSelectedCourse(course);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setSelectedCourse(null);
    setShowDialog(false);
    setMessage({ type: "", text: "" });
    setPreferredIntake("");
  };

  const handleApply = async () => {
    // Clear previous messages
    setMessage({ type: "", text: "" });

    if (!preferredIntake) {
      setMessage({
        type: "error",
        text: "Please select a preferred intake date before submitting.",
      });
      return;
    }

    try {
      setSubmitting(true);
      const authToken = sessionStorage.getItem("token");

      // First get student profile to get student ID
      const profileResponse = await api({
        url: `/api/user/student/profile`,
        method: "GET",
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (profileResponse.status === 200) {
        const studentId = profileResponse.data.data.id;

        const response = await api({
          url: `/api/new-course/create`,
          method: "POST",
          headers: { Authorization: `Bearer ${authToken}` },
          data: {
            course_id: selectedCourse.id,
            student_id: studentId,
            preferred_intake: preferredIntake,
          },
        });

        console.log(response.data);
        setMessage({
          type: "success",
          text: "Application submitted successfully! You will be notified once your application is reviewed.",
        });

        // Reset form and close dialog after a short delay to show success message
        setTimeout(() => {
          setPreferredIntake("");
          setMessage({ type: "", text: "" });
          closeDialog();
        }, 2000);
      } else {
        setMessage({
          type: "error",
          text: "Unable to fetch student profile. Please try again.",
        });
      }
    } catch (error) {
      console.log("Error:", error);
      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Error submitting application. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-8 pb-20 max-w-7xl min-h-screen">
        {loading ? (
          <div className="w-full flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 text-lg">Loading courses...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-8 w-full">
            {/* Header Section */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white text-2xl">📚</span>
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Available Courses
                  </h1>
                  <p className="text-gray-600 text-lg">
                    Browse and apply for courses that match your interests
                  </p>
                </div>
              </div>
            </div>

            {/* Search Section */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-purple-600 text-lg">🔍</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  Search Courses
                </h2>
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search courses by name..."
                  className="w-full p-4 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <span className="text-gray-400">🔍</span>
                </div>
              </div>
            </div>

            {/* Courses Section */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-indigo-600 text-lg">📋</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Course Catalog
                    </h2>
                  </div>
                  <div className="text-sm text-gray-500">
                    {filteredCourses.length} course
                    {filteredCourses.length !== 1 ? "s" : ""} found
                  </div>
                </div>
              </div>

              {filteredCourses.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-gray-400 text-2xl">📚</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {searchQuery
                      ? "No courses match your search"
                      : "No courses available"}
                  </h3>
                  <p className="text-gray-600">
                    {searchQuery
                      ? "Try adjusting your search terms"
                      : "Please check back later for new courses"}
                  </p>
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full bg-white">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Course Name
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredCourses.map((course) => (
                          <tr
                            key={course.id}
                            className="hover:bg-gray-50 transition-colors duration-150"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                                  <span className="text-green-600 text-lg">
                                    📖
                                  </span>
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {course.name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    DEPARTMENT: {course.department_name}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => openDialog(course)}
                                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                              >
                                Apply Now
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="md:hidden space-y-4 p-4">
                    {filteredCourses.map((course) => (
                      <div
                        key={course.id}
                        className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow duration-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center flex-1">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                              <span className="text-green-600 text-lg">📖</span>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                                {course.name}
                              </h3>
                              <p className="text-xs text-gray-500">
                                DEPARTMENT: {course.department_name}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => openDialog(course)}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg transition-colors duration-200 text-sm font-medium ml-3"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Modern Application Modal */}
        {showDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-green-500 to-blue-600 text-white p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-4">
                      <span className="text-white text-xl">📝</span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">Course Application</h2>
                      <p className="text-green-100">Submit your application</p>
                    </div>
                  </div>
                  <button
                    onClick={closeDialog}
                    className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center hover:bg-opacity-30 transition-all"
                  >
                    <span className="text-white text-lg">×</span>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selected Course
                  </label>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                        <span className="text-green-600">📖</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {selectedCourse?.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          Course ID: {selectedCourse?.id}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="preferredIntake"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Preferred Intake Date *
                  </label>
                  <input
                    type="date"
                    id="preferredIntake"
                    value={preferredIntake}
                    onChange={(e) => setPreferredIntake(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Select your preferred course start date
                  </p>
                </div>

                {/* Message Display */}
                {message.text && (
                  <div
                    className={`p-4 rounded-lg border ${
                      message.type === "success"
                        ? "bg-green-50 border-green-200 text-green-800"
                        : "bg-red-50 border-red-200 text-red-800"
                    }`}
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        {message.type === "success" ? (
                          <svg
                            className="h-5 w-5 text-green-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="h-5 w-5 text-red-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium">{message.text}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 p-6 rounded-b-2xl flex gap-3">
                <button
                  onClick={closeDialog}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-3 rounded-lg transition-colors duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white px-4 py-3 rounded-lg transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[140px]"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    "Submit Application"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseTable;
